import { useState, useMemo, useEffect } from 'react';
import { Card, Badge, SlidePanel, Modal, toast, PropertyImageUploader, PropertyPreviewModal } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './Properties.module.css';

interface Property {
  id: string; name: string; address: string; property_type: string; status: string;
  rent: number; management_fee: number; layout: string; area: number; built_year: number;
  nearest_station: string; walk_minutes: number; owner_name: string;
  portal_suumo: string; portal_homes: string; portal_athome: string;
  structure?: string; deposit?: number; deposit_months?: number; key_money?: number; key_money_months?: number;
  features?: string; assignee?: string; inquiry_count?: number;
  thumb_url?: string | null;
  zoning?: string; building_coverage?: number; floor_area_ratio?: number;
  land_area?: number; land_category?: string; house_number?: string;
  registration_owner?: string; mortgage?: string; other_rights?: string;
  water_supply?: string; sewage?: string; gas_type?: string; electricity?: number;
  asbestos?: string; earthquake_resistance?: string;
  building_inspection?: string; building_inspection_date?: string;
  hazard_flood?: string; hazard_landslide?: string; hazard_tsunami?: string; hazard_storm_surge?: string;
  management_form?: string;
  building_floors?: number; room_number?: string; floor_number?: string;
}

const statusLabel: Record<string, string> = { vacant: '募集中', applied: '申込中', occupied: '管理中', leaving: '退去予定', hidden: '非公開' };
const statusVariant: Record<string, 'green' | 'blue' | 'amber' | 'purple' | 'gray'> = { vacant: 'green', applied: 'blue', occupied: 'amber', leaving: 'purple', hidden: 'gray' };
const typeLabel: Record<string, string> = { rental: '賃貸', sales: '売買', management: '管理' };
const portalStatus = (s: string) => s === 'submitted' ? '✓' : s === 'error' ? '✕' : '—';
const portalVariant = (s: string): 'green' | 'red' | 'gray' => s === 'submitted' ? 'green' : s === 'error' ? 'red' : 'gray';
const layoutOptions = ['1R', '1K', '1DK', '1LDK', '2K', '2DK', '2LDK', '3K', '3DK', '3LDK', '4LDK', '5LDK+'];

export function Properties() {
  // 検索・ページング state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 検索 debounce (300ms)
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const offset = (page - 1) * pageSize;
  const listParams = new URLSearchParams({
    paginated: 'true',
    limit: String(pageSize),
    offset: String(offset),
    ...(search ? { q: search } : {}),
  });
  const { data: listData, loading, refetch } = useApi<{ items: Property[]; total: number }>(
    `/tenant/properties?${listParams.toString()}`
  );
  const properties = listData?.items ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [selected, setSelected] = useState<Property | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [relatedCounts, setRelatedCounts] = useState<{ contracts: number; minpaku_listings: number; portal_queue: number; minpaku_bookings: number; cleaning_tasks: number } | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterArea, setFilterArea] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // 登録フォーム
  const [form, setForm] = useState({
    name: '', address: '', property_type: 'rental', status: 'vacant',
    rent: '', layout: '1R', area: '', built_year: '', structure: '',
    nearest_station: '', walk_minutes: '', management_fee: '', owner_name: '',
    deposit_months: '2', key_money_months: '1', features: '', assignee: '',
    portal_suumo: true, portal_homes: true, portal_athome: true,
    building_floors: '', room_number: '', floor_number: '',
  });

  // 編集フォーム
  const [editForm, setEditForm] = useState({
    name: '', address: '', property_type: 'rental', status: 'vacant',
    rent: '', layout: '', area: '', built_year: '', structure: '',
    nearest_station: '', walk_minutes: '', management_fee: '',
    owner_name: '', deposit_months: '', key_money_months: '',
    features: '', assignee: '',
    portal_suumo: false, portal_homes: false, portal_athome: false,
    // 重要事項説明書用
    zoning: '', building_coverage: '', floor_area_ratio: '',
    land_area: '', land_category: '', house_number: '',
    registration_owner: '', mortgage: 'none', other_rights: '',
    water_supply: 'public', sewage: 'public', gas_type: 'city', electricity: '',
    asbestos: 'unknown', earthquake_resistance: 'unknown',
    building_inspection: 'none', building_inspection_date: '',
    hazard_flood: 'unknown', hazard_landslide: 'unknown',
    hazard_tsunami: 'unknown', hazard_storm_surge: 'unknown',
    management_form: '',
    building_floors: '', room_number: '', floor_number: '',
  });

  const areaOptions = useMemo(() => {
    const areas = new Set<string>();
    properties.forEach(p => { const match = p.address.match(/^(.+?[区市町村])/); if (match) areas.add(match[1]); });
    return Array.from(areas).sort();
  }, [properties]);

  const filtered = properties
    .filter(p => filterType === 'all' || p.property_type === filterType)
    .filter(p => filterArea === 'all' || p.address.startsWith(filterArea))
    .filter(p => filterStatus === 'all' || p.status === filterStatus);

  const resetForm = () => setForm({ name: '', address: '', property_type: 'rental', status: 'vacant', rent: '', layout: '1R', area: '', built_year: '', structure: '', nearest_station: '', walk_minutes: '', management_fee: '', owner_name: '', deposit_months: '2', key_money_months: '1', features: '', assignee: '', portal_suumo: true, portal_homes: true, portal_athome: true, building_floors: '', room_number: '', floor_number: '' });

  const handleCreate = async () => {
    // 必須バリデーション (業務観点で物件識別・顧客提示・ポータル入稿に最低限必要な9項目)
    const missing: string[] = [];
    if (!form.name.trim()) missing.push('物件名');
    if (!form.address.trim()) missing.push('住所');
    if (!form.property_type) missing.push('種別');
    if (!form.layout) missing.push('間取り');
    if (!form.rent || Number(form.rent) <= 0) missing.push('賃料・価格');
    if (!form.area || Number(form.area) <= 0) missing.push('専有面積');
    if (!form.nearest_station.trim()) missing.push('最寄駅');
    if (!form.walk_minutes || Number(form.walk_minutes) <= 0) missing.push('徒歩(分)');
    if (!form.built_year || Number(form.built_year) <= 0) missing.push('築年数');
    if (!form.structure.trim()) missing.push('構造');
    if (missing.length) {
      toast(`以下の項目を入力してください: ${missing.join('、')}`, 'error');
      return;
    }
    try {
      const created = await api<Property>('/tenant/properties', { method: 'POST', body: JSON.stringify({
        name: form.name, address: form.address, property_type: form.property_type, status: form.status,
        rent: Number(form.rent) || 0, layout: form.layout, area: Number(form.area) || 0,
        built_year: Number(form.built_year) || 0, structure: form.structure || null,
        walk_minutes: Number(form.walk_minutes) || 0,
        management_fee: Number(form.management_fee) || 0,
        nearest_station: form.nearest_station, owner_name: form.owner_name,
        deposit: Number(form.deposit_months) || 0,
        key_money: Number(form.key_money_months) || 0,
        features: form.features || null,
        assignee: form.assignee || null,
        portal_suumo: form.portal_suumo ? 'submitted' : 'none',
        portal_homes: form.portal_homes ? 'submitted' : 'none',
        portal_athome: form.portal_athome ? 'submitted' : 'none',
        building_floors: Number(form.building_floors) || null,
        room_number: form.room_number || null,
        floor_number: form.floor_number || null,
      })});
      toast('物件を登録しました。続けて物件画像を追加してください', 'success');
      setShowCreate(false);
      resetForm();
      refetch();
      // 登録した物件の編集画面を自動で開く (画像アップロード導線)
      setSelected(created);
      openEditMode(created);
    } catch (e) { toast((e as Error)?.message || '登録に失敗しました', 'error'); }
  };

  // 編集モードを開く
  const openEditMode = (target?: Property) => {
    const p = target ?? selected;
    if (!p) return;
    setEditForm({
      name: p.name, address: p.address,
      property_type: p.property_type, status: p.status,
      rent: String(p.rent || ''), layout: p.layout || '',
      area: String(p.area || ''), built_year: String(p.built_year || ''),
      structure: p.structure || '',
      nearest_station: p.nearest_station || '',
      walk_minutes: String(p.walk_minutes || ''),
      management_fee: String(p.management_fee || ''),
      owner_name: p.owner_name || '',
      deposit_months: String(p.deposit ?? p.deposit_months ?? ''),
      key_money_months: String(p.key_money ?? p.key_money_months ?? ''),
      features: p.features || '', assignee: p.assignee || '',
      portal_suumo: p.portal_suumo === 'submitted',
      portal_homes: p.portal_homes === 'submitted',
      portal_athome: p.portal_athome === 'submitted',
      zoning: p.zoning || '',
      building_coverage: String(p.building_coverage || ''),
      floor_area_ratio: String(p.floor_area_ratio || ''),
      land_area: String(p.land_area || ''),
      land_category: p.land_category || '',
      house_number: p.house_number || '',
      registration_owner: p.registration_owner || '',
      mortgage: p.mortgage || 'none',
      other_rights: p.other_rights || '',
      water_supply: p.water_supply || 'public',
      sewage: p.sewage || 'public',
      gas_type: p.gas_type || 'city',
      electricity: String(p.electricity || ''),
      asbestos: p.asbestos || 'unknown',
      earthquake_resistance: p.earthquake_resistance || 'unknown',
      building_inspection: p.building_inspection || 'none',
      building_inspection_date: p.building_inspection_date || '',
      hazard_flood: p.hazard_flood || 'unknown',
      hazard_landslide: p.hazard_landslide || 'unknown',
      hazard_tsunami: p.hazard_tsunami || 'unknown',
      hazard_storm_surge: p.hazard_storm_surge || 'unknown',
      management_form: p.management_form || '',
      building_floors: String(p.building_floors || ''),
      room_number: p.room_number || '',
      floor_number: p.floor_number || '',
    });
    setEditMode(true);
  };

  // 保存
  const handleSaveEdit = async () => {
    if (!selected) return;
    try {
      const updated = await api<Property>(`/tenant/properties/${selected.id}`, { method: 'PATCH', body: JSON.stringify({
        name: editForm.name, address: editForm.address,
        property_type: editForm.property_type, status: editForm.status,
        rent: Number(editForm.rent) || 0, layout: editForm.layout,
        area: Number(editForm.area) || 0, structure: editForm.structure,
        built_year: Number(editForm.built_year) || null,
        nearest_station: editForm.nearest_station,
        walk_minutes: Number(editForm.walk_minutes) || 0,
        management_fee: Number(editForm.management_fee) || 0,
        owner_name: editForm.owner_name,
        deposit: Number(editForm.deposit_months) || 0,
        key_money: Number(editForm.key_money_months) || 0,
        features: editForm.features || null,
        assignee: editForm.assignee || null,
        portal_suumo: editForm.portal_suumo ? 'submitted' : 'none',
        portal_homes: editForm.portal_homes ? 'submitted' : 'none',
        portal_athome: editForm.portal_athome ? 'submitted' : 'none',
        zoning: editForm.zoning || null,
        building_coverage: Number(editForm.building_coverage) || null,
        floor_area_ratio: Number(editForm.floor_area_ratio) || null,
        land_area: Number(editForm.land_area) || null,
        land_category: editForm.land_category || null,
        house_number: editForm.house_number || null,
        registration_owner: editForm.registration_owner || null,
        mortgage: editForm.mortgage, other_rights: editForm.other_rights || null,
        water_supply: editForm.water_supply, sewage: editForm.sewage,
        gas_type: editForm.gas_type,
        electricity: Number(editForm.electricity) || null,
        asbestos: editForm.asbestos, earthquake_resistance: editForm.earthquake_resistance,
        building_inspection: editForm.building_inspection,
        building_inspection_date: editForm.building_inspection_date || null,
        hazard_flood: editForm.hazard_flood, hazard_landslide: editForm.hazard_landslide,
        hazard_tsunami: editForm.hazard_tsunami, hazard_storm_surge: editForm.hazard_storm_surge,
        management_form: editForm.management_form || null,
        building_floors: Number(editForm.building_floors) || null,
        room_number: editForm.room_number || null,
        floor_number: editForm.floor_number || null,
      })});
      toast('物件情報を更新しました', 'success');
      setEditMode(false); setSelected(updated); refetch();
    } catch { toast('更新に失敗しました', 'error'); }
  };

  // 削除
  const handleDelete = async () => {
    if (!selected) return;
    try {
      await api(`/tenant/properties/${selected.id}`, { method: 'DELETE' });
      toast('物件を削除しました', 'success');
      setShowDelete(false); setSelected(null); setEditMode(false); refetch();
    } catch { toast('削除に失敗しました', 'error'); }
  };

  const handleExport = async () => {
    // CSV は検索条件(search)に合う全件を再取得してから出力。
    // クライアント側フィルタ(type/area/status)は追加で適用。
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      const path = params.toString() ? `/tenant/properties?${params}` : '/tenant/properties';
      const all = await api<Property[]>(path);
      const target = all
        .filter(p => filterType === 'all' || p.property_type === filterType)
        .filter(p => filterArea === 'all' || p.address.startsWith(filterArea))
        .filter(p => filterStatus === 'all' || p.status === filterStatus);
      if (!target.length) { toast('出力するデータがありません', 'error'); return; }
      const headers = ['物件名', '住所', '種別', '間取り', '面積(m²)', '賃料(円)', 'ステータス', '最寄駅', 'オーナー'];
      const rows = target.map(p => [p.name, p.address, typeLabel[p.property_type] ?? p.property_type, p.layout || '', p.area || '', p.rent || '', statusLabel[p.status] ?? p.status, p.nearest_station ? `${p.nearest_station} 徒歩${p.walk_minutes}分` : '', p.owner_name || '']);
      const csv = '\uFEFF' + [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `物件一覧_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast(`CSVファイルをダウンロードしました (${target.length} 件)`, 'success');
    } catch {
      toast('CSV出力に失敗しました', 'error');
    }
  };

  const closePanel = () => { setSelected(null); setEditMode(false); };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filterRow}>
          <input
            className={styles.filterSelect}
            type="search"
            placeholder="🔍 物件名・住所・駅名で検索"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <select className={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">全種別</option><option value="rental">賃貸</option><option value="sales">売買</option><option value="management">管理</option>
          </select>
          <select className={styles.filterSelect} value={filterArea} onChange={e => setFilterArea(e.target.value)}>
            <option value="all">全エリア</option>
            {areaOptions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">全ステータス</option><option value="vacant">募集中</option><option value="applied">申込中</option><option value="occupied">管理中</option><option value="leaving">退去予定</option><option value="hidden">非公開</option>
          </select>
        </div>
        <div className={styles.actions}>
          <button className={styles.btnOutline} onClick={handleExport}>🖨 出力</button>
          <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>＋ 物件登録</button>
        </div>
      </div>

      <Card title={`物件一覧(全 ${total} 件 / 表示 ${filtered.length} 件)`}>
        {/* ページャ(中央) + 表示件数(右) */}
        {total > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, padding: '4px 4px 12px', borderBottom: '1px solid var(--border)' }}>
            <div />
            <div style={{ justifySelf: 'center', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink2)' }}>
              <button className={styles.btnOutline} style={{ padding: '6px 14px' }} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading}>‹ 前へ</button>
              <span><strong style={{ fontFamily: 'var(--mono)' }}>{page}</strong> / {totalPages} ページ <span style={{ color: 'var(--ink3)', marginLeft: 4 }}>(全 {total} 件)</span></span>
              <button className={styles.btnOutline} style={{ padding: '6px 14px' }} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>次へ ›</button>
            </div>
            <div style={{ justifySelf: 'end', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink2)' }}>
              <label>表示:</label>
              <select className={styles.filterSelect} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ minWidth: 80 }}>
                <option value={10}>10 件</option>
                <option value={20}>20 件</option>
                <option value={50}>50 件</option>
              </select>
            </div>
          </div>
        )}
        {loading ? <div style={{ padding: 20, color: 'var(--ink3)' }}>読込中...</div> : (
          <table className={styles.table}>
            <thead><tr>
              <th className={styles.th}>物件名・住所</th>
              <th className={styles.th} style={{ width: 64 }}>物件画像</th>
              <th className={styles.th}>種別</th><th className={styles.th}>間取り</th>
              <th className={styles.th}>面積</th><th className={styles.th}>賃料・価格</th><th className={styles.th}>ステータス</th>
              <th className={styles.th}>入稿</th><th className={styles.th}>問合せ</th>
              <th className={styles.th} style={{ width: 100 }}>物件紹介</th>
            </tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className={styles.tr} onClick={() => { setSelected(p); setEditMode(false); }}>
                  <td className={styles.td}><span style={{ fontWeight: 600 }}>{p.name}</span><br /><span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{p.address}</span></td>
                  <td className={styles.td}>
                    {p.thumb_url ? (
                      <img src={p.thumb_url} alt="" className={styles.thumbImg} />
                    ) : (
                      <div className={styles.thumbEmpty}>📷</div>
                    )}
                  </td>
                  <td className={styles.td}><Badge variant="blue">{typeLabel[p.property_type] ?? p.property_type}</Badge></td>
                  <td className={styles.td}>{p.layout || '—'}</td>
                  <td className={styles.td}>{p.area ? `${p.area}m²` : '—'}</td>
                  <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>¥{p.rent?.toLocaleString()}/月</td>
                  <td className={styles.td}><Badge variant={statusVariant[p.status] ?? 'gray'}>{statusLabel[p.status] ?? p.status}</Badge></td>
                  <td className={styles.td}><span style={{ fontSize: '11px' }}><Badge variant={portalVariant(p.portal_suumo)}>S{portalStatus(p.portal_suumo)}</Badge>{' '}<Badge variant={portalVariant(p.portal_homes)}>H{portalStatus(p.portal_homes)}</Badge>{' '}<Badge variant={portalVariant(p.portal_athome)}>A{portalStatus(p.portal_athome)}</Badge></span></td>
                  <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: (p.inquiry_count ?? 0) > 0 ? undefined : 'var(--ink3)' }}>{(p.inquiry_count ?? 0) > 0 ? p.inquiry_count : '—'}</td>
                  <td className={styles.td}>
                    <button
                      className={styles.previewBtn}
                      onClick={(e) => { e.stopPropagation(); setSelected(p); setShowPreview(true); }}
                      title="物件紹介書を表示"
                    >📄 物件紹介</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--ink4)' }}>物件が見つかりません</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      {/* ===== 物件詳細/編集 SlidePanel ===== */}
      <SlidePanel open={!!selected && !showPreview} onClose={closePanel} title={selected?.name ?? ''} width={560}
        actions={!editMode ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={styles.btnOutline} onClick={async () => {
              if (!selected) return;
              try {
                const counts = await api<{ contracts: number; minpaku_listings: number; portal_queue: number; minpaku_bookings: number; cleaning_tasks: number }>(`/tenant/properties/${selected.id}/related`);
                setRelatedCounts(counts);
              } catch { setRelatedCounts(null); }
              setShowDelete(true);
            }}>🗑 削除</button>
            <button className={styles.btnOutline} onClick={() => setShowPreview(true)}>📄 物件紹介</button>
            <button className={styles.btnPrimary} onClick={() => openEditMode()}>✏️ 編集</button>
          </div>
        ) : undefined}>
        {selected && !editMode && (
          <>
            <PanelSection title="基本情報">
              <SpecGrid>
                <SpecCell label="所在地" value={selected.address} />
                <SpecCell label="種別" value={typeLabel[selected.property_type] ?? selected.property_type} />
                <SpecCell label="築年" value={selected.built_year ? `${selected.built_year}年` : '—'} />
                <SpecCell label="構造" value={selected.structure || '—'} />
                <SpecCell label="建物階数" value={selected.building_floors ? `${selected.building_floors}階建` : '—'} />
                <SpecCell label="部屋番号" value={selected.room_number || '—'} />
                <SpecCell label="所在階" value={selected.floor_number || '—'} />
                <SpecCell label="ステータス" value={statusLabel[selected.status] ?? selected.status} />
                <SpecCell label="管理費" value={selected.management_fee ? `¥${selected.management_fee.toLocaleString()}` : '—'} mono />
              </SpecGrid>
            </PanelSection>
            <PanelSection title="賃貸条件">
              <SpecGrid>
                <SpecCell label="賃料" value={`¥${selected.rent?.toLocaleString()}`} mono />
                <SpecCell label="間取り" value={selected.layout || '—'} />
                <SpecCell label="面積" value={selected.area ? `${selected.area}m²` : '—'} mono />
                <SpecCell label="最寄駅" value={selected.nearest_station ? `${selected.nearest_station} 徒歩${selected.walk_minutes}分` : '—'} />
                <SpecCell label="敷金" value={(selected.deposit ?? selected.deposit_months) ? `${selected.deposit ?? selected.deposit_months}ヶ月` : '—'} />
                <SpecCell label="礼金" value={(selected.key_money ?? selected.key_money_months) ? `${selected.key_money ?? selected.key_money_months}ヶ月` : '—'} />
              </SpecGrid>
            </PanelSection>
            {selected.features && (
              <PanelSection title="設備・特記事項">
                <div style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.6 }}>{selected.features}</div>
              </PanelSection>
            )}
            <PanelSection title="ポータル入稿状況">
              <div className={styles.portalGrid}>
                {[['SUUMO', selected.portal_suumo], ["HOME'S", selected.portal_homes], ['athome', selected.portal_athome]].map(([name, status]) => (
                  <div key={name as string} className={styles.portalItem}>
                    <span className={styles.portalName}>{name}</span>
                    <Badge variant={portalVariant(status as string)}>{status === 'submitted' ? '入稿済' : status === 'error' ? 'エラー' : '未入稿'}</Badge>
                  </div>
                ))}
              </div>
            </PanelSection>
            <PanelSection title="担当者・オーナー">
              <SpecGrid>
                <SpecCell label="担当者" value={selected.assignee || '—'} />
                <SpecCell label="オーナー" value={selected.owner_name || '—'} />
              </SpecGrid>
            </PanelSection>
          </>
        )}

        {/* ===== 編集モード ===== */}
        {selected && editMode && (
          <div className={styles.editPanel}>
            <PanelSection title="📷 物件画像">
              <PropertyImageUploader propertyId={selected.id} onChange={refetch} />
            </PanelSection>
            <PanelSection title="基本情報">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>物件名</label><input className={styles.input} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>種別</label><select className={styles.input} value={editForm.property_type} onChange={e => setEditForm({ ...editForm, property_type: e.target.value })}><option value="rental">賃貸</option><option value="sales">売買</option><option value="management">管理</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>住所</label><input className={styles.input} value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>構造</label><input className={styles.input} value={editForm.structure} onChange={e => setEditForm({ ...editForm, structure: e.target.value })} placeholder="RC造" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>建物階数</label><input className={styles.input} type="number" value={editForm.building_floors} onChange={e => setEditForm({ ...editForm, building_floors: e.target.value })} placeholder="5" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>部屋番号</label><input className={styles.input} value={editForm.room_number} onChange={e => setEditForm({ ...editForm, room_number: e.target.value })} placeholder="301" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>所在階</label><input className={styles.input} value={editForm.floor_number} onChange={e => setEditForm({ ...editForm, floor_number: e.target.value })} placeholder="3階" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>ステータス</label><select className={styles.input} value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                  {Object.entries(statusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>管理費</label><input className={styles.input} type="number" value={editForm.management_fee} onChange={e => setEditForm({ ...editForm, management_fee: e.target.value })} /></div>
              </div>
            </PanelSection>

            <PanelSection title="賃貸条件">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>賃料</label><input className={styles.input} type="number" value={editForm.rent} onChange={e => setEditForm({ ...editForm, rent: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>間取り</label><select className={styles.input} value={editForm.layout} onChange={e => setEditForm({ ...editForm, layout: e.target.value })}>{layoutOptions.map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>面積（m²）</label><input className={styles.input} type="number" value={editForm.area} onChange={e => setEditForm({ ...editForm, area: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>築年数</label><input className={styles.input} type="number" value={editForm.built_year} onChange={e => setEditForm({ ...editForm, built_year: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>最寄駅</label><input className={styles.input} value={editForm.nearest_station} onChange={e => setEditForm({ ...editForm, nearest_station: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>徒歩（分）</label><input className={styles.input} type="number" value={editForm.walk_minutes} onChange={e => setEditForm({ ...editForm, walk_minutes: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>敷金（ヶ月）</label><input className={styles.input} type="number" value={editForm.deposit_months} onChange={e => setEditForm({ ...editForm, deposit_months: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>礼金（ヶ月）</label><input className={styles.input} type="number" value={editForm.key_money_months} onChange={e => setEditForm({ ...editForm, key_money_months: e.target.value })} /></div>
              </div>
            </PanelSection>

            <PanelSection title="設備・担当">
              <div className={styles.formGroup}><label className={styles.formLabel}>設備・特記事項</label><textarea className={styles.textarea} value={editForm.features} onChange={e => setEditForm({ ...editForm, features: e.target.value })} rows={2} /></div>
              <div className={styles.editGrid} style={{ marginTop: 10 }}>
                <div className={styles.formGroup}><label className={styles.formLabel}>担当者</label><input className={styles.input} value={editForm.assignee} onChange={e => setEditForm({ ...editForm, assignee: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>オーナー</label><input className={styles.input} value={editForm.owner_name} onChange={e => setEditForm({ ...editForm, owner_name: e.target.value })} /></div>
              </div>
            </PanelSection>

            <PanelSection title="登記・法的情報">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>登記所有者</label><input className={styles.input} value={editForm.registration_owner} onChange={e => setEditForm({ ...editForm, registration_owner: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>家屋番号</label><input className={styles.input} value={editForm.house_number} onChange={e => setEditForm({ ...editForm, house_number: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>抵当権</label><select className={styles.input} value={editForm.mortgage} onChange={e => setEditForm({ ...editForm, mortgage: e.target.value })}><option value="none">無</option><option value="yes">有</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>用途地域</label><input className={styles.input} value={editForm.zoning} onChange={e => setEditForm({ ...editForm, zoning: e.target.value })} placeholder="第一種住居地域" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>建蔽率（%）</label><input className={styles.input} type="number" value={editForm.building_coverage} onChange={e => setEditForm({ ...editForm, building_coverage: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>容積率（%）</label><input className={styles.input} type="number" value={editForm.floor_area_ratio} onChange={e => setEditForm({ ...editForm, floor_area_ratio: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>地目</label><input className={styles.input} value={editForm.land_category} onChange={e => setEditForm({ ...editForm, land_category: e.target.value })} placeholder="宅地" /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>土地面積（m²）</label><input className={styles.input} type="number" value={editForm.land_area} onChange={e => setEditForm({ ...editForm, land_area: e.target.value })} /></div>
              </div>
              <div className={styles.formGroup} style={{ marginTop: 8 }}><label className={styles.formLabel}>その他の権利</label><textarea className={styles.textarea} value={editForm.other_rights} onChange={e => setEditForm({ ...editForm, other_rights: e.target.value })} rows={2} /></div>
            </PanelSection>

            <PanelSection title="設備（水道・電気・ガス）">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>上水道</label><select className={styles.input} value={editForm.water_supply} onChange={e => setEditForm({ ...editForm, water_supply: e.target.value })}><option value="public">公営</option><option value="private">私営</option><option value="other">その他</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>下水道</label><select className={styles.input} value={editForm.sewage} onChange={e => setEditForm({ ...editForm, sewage: e.target.value })}><option value="public">公共下水</option><option value="septic">浄化槽</option><option value="other">その他</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>ガス</label><select className={styles.input} value={editForm.gas_type} onChange={e => setEditForm({ ...editForm, gas_type: e.target.value })}><option value="city">都市ガス</option><option value="propane">プロパン</option><option value="other">その他</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>電気（A）</label><input className={styles.input} type="number" value={editForm.electricity} onChange={e => setEditForm({ ...editForm, electricity: e.target.value })} /></div>
              </div>
            </PanelSection>

            <PanelSection title="建物の状況">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>アスベスト調査</label><select className={styles.input} value={editForm.asbestos} onChange={e => setEditForm({ ...editForm, asbestos: e.target.value })}><option value="unknown">不明</option><option value="yes">有</option><option value="no">無</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>耐震診断</label><select className={styles.input} value={editForm.earthquake_resistance} onChange={e => setEditForm({ ...editForm, earthquake_resistance: e.target.value })}><option value="unknown">不明</option><option value="yes">有</option><option value="no">無</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>建物状況調査</label><select className={styles.input} value={editForm.building_inspection} onChange={e => setEditForm({ ...editForm, building_inspection: e.target.value })}><option value="none">無</option><option value="yes">有</option></select></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>調査実施日</label><input className={styles.input} type="date" value={editForm.building_inspection_date} onChange={e => setEditForm({ ...editForm, building_inspection_date: e.target.value })} /></div>
              </div>
            </PanelSection>

            <PanelSection title="ハザードマップ">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>洪水</label>
                  <select className={styles.input} value={editForm.hazard_flood} onChange={e => setEditForm({ ...editForm, hazard_flood: e.target.value })}>
                    <option value="unknown">不明</option>
                    <option value="outside">区域外</option>
                    <option value="warning">警戒区域</option>
                    <option value="danger">特別警戒区域</option>
                  </select>
                </div>
                <div className={styles.formGroup}><label className={styles.formLabel}>土砂災害</label>
                  <select className={styles.input} value={editForm.hazard_landslide} onChange={e => setEditForm({ ...editForm, hazard_landslide: e.target.value })}>
                    <option value="unknown">不明</option>
                    <option value="outside">区域外</option>
                    <option value="warning">警戒区域</option>
                    <option value="danger">特別警戒区域</option>
                  </select>
                </div>
                <div className={styles.formGroup}><label className={styles.formLabel}>津波</label>
                  <select className={styles.input} value={editForm.hazard_tsunami} onChange={e => setEditForm({ ...editForm, hazard_tsunami: e.target.value })}>
                    <option value="unknown">不明</option>
                    <option value="outside">区域外</option>
                    <option value="warning">警戒区域</option>
                    <option value="danger">特別警戒区域</option>
                  </select>
                </div>
                <div className={styles.formGroup}><label className={styles.formLabel}>高潮</label>
                  <select className={styles.input} value={editForm.hazard_storm_surge} onChange={e => setEditForm({ ...editForm, hazard_storm_surge: e.target.value })}>
                    <option value="unknown">不明</option>
                    <option value="outside">区域外</option>
                    <option value="warning">警戒区域</option>
                    <option value="danger">特別警戒区域</option>
                  </select>
                </div>
              </div>
            </PanelSection>

            <PanelSection title="管理">
              <div className={styles.editGrid}>
                <div className={styles.formGroup}><label className={styles.formLabel}>管理形態</label><input className={styles.input} value={editForm.management_form} onChange={e => setEditForm({ ...editForm, management_form: e.target.value })} placeholder="全部委託" /></div>
              </div>
            </PanelSection>

            <PanelSection title="ポータル入稿">
              <div className={styles.portalCheckboxes}>
                {[['suumo', 'SUUMO'], ['homes', "HOME'S"], ['athome', 'athome']].map(([key, name]) => (
                  <label key={key} className={styles.checkboxLabel}>
                    <input type="checkbox" checked={editForm[`portal_${key}` as keyof typeof editForm] as boolean} onChange={e => setEditForm({ ...editForm, [`portal_${key}`]: e.target.checked })} />
                    <span className={styles.checkboxText}>{name}</span>
                  </label>
                ))}
              </div>
            </PanelSection>

            <div className={styles.editFooter}>
              <button className={styles.btnOutline} onClick={() => setEditMode(false)}>キャンセル</button>
              <button className={styles.btnPrimary} onClick={handleSaveEdit}>保存</button>
            </div>
          </div>
        )}
      </SlidePanel>

      {/* 削除確認モーダル */}
      <Modal open={showDelete} onClose={() => setShowDelete(false)} title="物件削除の確認" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btnOutline} onClick={() => setShowDelete(false)}>キャンセル</button>
          <button className={styles.deleteBtn} onClick={handleDelete}>削除する</button>
        </div>
      }>
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <p><strong>{selected?.name}</strong> を削除しますか？</p>
          {relatedCounts && (relatedCounts.contracts + relatedCounts.minpaku_listings + relatedCounts.portal_queue + relatedCounts.minpaku_bookings + relatedCounts.cleaning_tasks) > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 14px', margin: '12px 0', fontSize: 12, color: '#991b1b' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>この物件には以下の関連データがあります:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {relatedCounts.contracts > 0 && <li>契約・書類: {relatedCounts.contracts}件（property_idをNULLに更新）</li>}
                {relatedCounts.portal_queue > 0 && <li>ポータル入稿キュー: {relatedCounts.portal_queue}件（削除）</li>}
                {relatedCounts.minpaku_listings > 0 && <li>民泊物件登録: {relatedCounts.minpaku_listings}件（削除）</li>}
                {relatedCounts.minpaku_bookings > 0 && <li>民泊予約: {relatedCounts.minpaku_bookings}件（削除）</li>}
                {relatedCounts.cleaning_tasks > 0 && <li>清掃タスク: {relatedCounts.cleaning_tasks}件（削除）</li>}
              </ul>
            </div>
          )}
          <p style={{ color: 'var(--red)', fontSize: 12 }}>この操作は取り消せません。</p>
        </div>
      </Modal>

      {/* 物件登録モーダル（既存） */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); resetForm(); }} title="🏢 物件登録" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btnOutline} onClick={() => { setShowCreate(false); resetForm(); }}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleCreate}>登録して画像追加 →</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>物件基本情報 <span className={styles.requiredHint}>（<span className={styles.requiredMark}>*</span>は必須）</span></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>物件名 <span className={styles.requiredMark}>*</span></label><input className={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="例：グランドヒルズ渋谷" /></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>種別 <span className={styles.requiredMark}>*</span></label><select className={styles.input} value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })}><option value="rental">賃貸</option><option value="sales">売買</option><option value="management">管理</option></select></div><div /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>住所 <span className={styles.requiredMark}>*</span></label><input className={styles.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="例：渋谷区神南1-12-3 5F" /><span className={styles.formHint}>郵便番号から住所を自動入力できます</span></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>間取り <span className={styles.requiredMark}>*</span></label><select className={styles.input} value={form.layout} onChange={e => setForm({ ...form, layout: e.target.value })}>{layoutOptions.map(l => <option key={l} value={l}>{l}</option>)}</select></div><div className={styles.formGroup}><label className={styles.formLabel}>専有面積（m²） <span className={styles.requiredMark}>*</span></label><input className={styles.input} type="number" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="62.4" /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>賃料・売買価格（円） <span className={styles.requiredMark}>*</span></label><input className={styles.input} type="number" value={form.rent} onChange={e => setForm({ ...form, rent: e.target.value })} placeholder="198,000" /></div><div className={styles.formGroup}><label className={styles.formLabel}>築年数 <span className={styles.requiredMark}>*</span></label><input className={styles.input} type="number" value={form.built_year} onChange={e => setForm({ ...form, built_year: e.target.value })} placeholder="8" /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>敷金（ヶ月）</label><input className={styles.input} type="number" value={form.deposit_months} onChange={e => setForm({ ...form, deposit_months: e.target.value })} /></div><div className={styles.formGroup}><label className={styles.formLabel}>礼金（ヶ月）</label><input className={styles.input} type="number" value={form.key_money_months} onChange={e => setForm({ ...form, key_money_months: e.target.value })} /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>最寄駅 <span className={styles.requiredMark}>*</span></label><input className={styles.input} value={form.nearest_station} onChange={e => setForm({ ...form, nearest_station: e.target.value })} placeholder="渋谷駅" /></div><div className={styles.formGroup}><label className={styles.formLabel}>徒歩（分） <span className={styles.requiredMark}>*</span></label><input className={styles.input} type="number" value={form.walk_minutes} onChange={e => setForm({ ...form, walk_minutes: e.target.value })} /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>構造 <span className={styles.requiredMark}>*</span></label><input className={styles.input} value={form.structure} onChange={e => setForm({ ...form, structure: e.target.value })} placeholder="RC造" /></div><div className={styles.formGroup}><label className={styles.formLabel}>管理費</label><input className={styles.input} type="number" value={form.management_fee} onChange={e => setForm({ ...form, management_fee: e.target.value })} /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>建物階数</label><input className={styles.input} type="number" value={form.building_floors} onChange={e => setForm({ ...form, building_floors: e.target.value })} placeholder="5" /></div><div className={styles.formGroup}><label className={styles.formLabel}>部屋番号</label><input className={styles.input} value={form.room_number} onChange={e => setForm({ ...form, room_number: e.target.value })} placeholder="301" /></div></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>所在階</label><input className={styles.input} value={form.floor_number} onChange={e => setForm({ ...form, floor_number: e.target.value })} placeholder="3階" /></div><div /></div>
            <div className={styles.formRow2}><div className={styles.formGroup}><label className={styles.formLabel}>オーナー</label><input className={styles.input} value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} placeholder="鈴木 太郎" /></div><div className={styles.formGroup}><label className={styles.formLabel}>担当者</label><input className={styles.input} value={form.assignee} onChange={e => setForm({ ...form, assignee: e.target.value })} placeholder="田村 誠司" /></div></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>設備・特記事項</label><textarea className={styles.textarea} value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} placeholder="ペット可・駐車場あり・オートロック・南向き..." rows={2} /></div>
          </div>
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>自動入稿（Standardプラン：3媒体まで）</div>
            <div className={styles.portalCheckboxes}>
              <label className={styles.checkboxLabel}><input type="checkbox" checked={form.portal_suumo} onChange={e => setForm({ ...form, portal_suumo: e.target.checked })} /><span className={styles.checkboxText}>SUUMO</span></label>
              <label className={styles.checkboxLabel}><input type="checkbox" checked={form.portal_homes} onChange={e => setForm({ ...form, portal_homes: e.target.checked })} /><span className={styles.checkboxText}>HOME'S</span></label>
              <label className={styles.checkboxLabel}><input type="checkbox" checked={form.portal_athome} onChange={e => setForm({ ...form, portal_athome: e.target.checked })} /><span className={styles.checkboxText}>athome</span></label>
            </div>
          </div>
          <div className={styles.createInfoBanner}>
            📷 物件画像(外観・間取り図・部屋写真)は登録後に編集パネルで続けて追加できます
          </div>
        </div>
      </Modal>

      {/* 物件紹介プレビュー */}
      <PropertyPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        propertyId={selected?.id ?? null}
        propertyName={selected?.name}
      />
    </div>
  );
}
